import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { type UserPreferenceKey } from 'utils/userPreferenceKeys';
import axiosInstance from 'api/axios';

interface UserPreferencesContextType {
  getPreference: (key: UserPreferenceKey, asBoolean?: boolean) => any;
  updatePreference: (key: UserPreferenceKey, value: any) => Promise<void>;

  // Initialize preferences from API (for login)
  initializeFromApi: (apiPreferences: any) => void;

  // Fetch preferences from backend (lazy loading)
  fetchFromBackend: () => Promise<void>;

  // Check if preferences are loaded
  isLoaded: boolean;

  // Check if we need to fetch from backend
  shouldFetchFromBackend: boolean;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | null>(
  null
);

export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error(
      'useUserPreferences must be used within a UserPreferencesProvider'
    );
  }
  return context;
};

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export const UserPreferencesProvider: React.FC<
  UserPreferencesProviderProps
> = ({ children }) => {
  const [preferences, setPreferences] = useState<Record<string, any>>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasAttemptedBackendFetch, setHasAttemptedBackendFetch] =
    useState(false);
  const [shouldFetchFromBackend, setShouldFetchFromBackend] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const storedPreferences = localStorage.getItem('userPreferences');
      if (storedPreferences) {
        const parsed = JSON.parse(storedPreferences);
        setPreferences(parsed || {});
        setHasAttemptedBackendFetch(true);
      } else {
        // Check if we've already tried fetching from backend before
        const backendFetchAttempted = localStorage.getItem(
          'userPreferences_backendFetched'
        );

        if (backendFetchAttempted) {
          setHasAttemptedBackendFetch(true);
        } else {
          setShouldFetchFromBackend(true);
        }
      }
    } catch (error) {
      console.warn('Failed to load user preferences:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save to localStorage whenever preferences change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem('userPreferences', JSON.stringify(preferences));
      } catch (error) {
        console.error('Failed to save user preferences:', error);
      }
    }
  }, [preferences, isLoaded]);

  const getPreference = useCallback(
    (key: UserPreferenceKey, asBoolean: boolean = false): any => {
      const value = preferences[key];

      if (asBoolean && typeof value === 'string') {
        return value === 'ACTIVE' || value === 'active';
      }

      return value;
    },
    [preferences]
  );

  const updatePreference = useCallback(
    async (key: UserPreferenceKey, value: any): Promise<void> => {
      const stringValue =
        typeof value === 'boolean' ? (value ? 'ACTIVE' : 'INACTIVE') : value;

      setPreferences((prev) => ({
        ...prev,
        [key]: stringValue,
      }));

      try {
        await axiosInstance.post('/user_preferences/create_or_update', {
          preference_key: key,
          status: stringValue,
        });
      } catch (error) {
        console.error('Failed to update user preference');
      }
    },
    []
  );

  const initializeFromApi = useCallback((apiPreferences: any): void => {
    if (apiPreferences) {
      if (Array.isArray(apiPreferences)) {
        const prefsObject: Record<string, any> = {};
        apiPreferences.forEach((pref: any) => {
          if (pref.preference_key) {
            prefsObject[pref.preference_key] = pref.status;
          }
        });
        setPreferences(prefsObject);
      }
      // Handle object format
      else if (typeof apiPreferences === 'object') {
        setPreferences(apiPreferences);
      }
    }
  }, []);

  const fetchFromBackend = useCallback(async (): Promise<void> => {
    if (hasAttemptedBackendFetch) return;
    setIsLoaded(false);

    try {
      setHasAttemptedBackendFetch(true);
      localStorage.setItem('userPreferences_backendFetched', 'true');

      const response = await axiosInstance.get('/user_preferences/');
      if (response.data) {
        const preferencesData = response.data.user_preferences || response.data;

        if (Array.isArray(preferencesData)) {
          const prefsObject: Record<string, any> = {};
          preferencesData.forEach((pref: any) => {
            if (pref.preference_key) {
              prefsObject[pref.preference_key] = pref.status;
            }
          });
          setPreferences(prefsObject);
        } else {
          setPreferences(preferencesData);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user preferences from backend');
    } finally {
      setIsLoaded(true);
    }
  }, [hasAttemptedBackendFetch]);

  // Auto-fetch from backend when conditions are met
  useEffect(() => {
    if (shouldFetchFromBackend) {
      void fetchFromBackend();
    }
  }, [shouldFetchFromBackend, fetchFromBackend]);

  const contextValue: UserPreferencesContextType = {
    getPreference,
    updatePreference,
    initializeFromApi,
    fetchFromBackend,
    isLoaded,
    shouldFetchFromBackend,
  };

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
